import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { STAGE, fmtPeriod, fmtInt } from "@/lib/format";
import { TrendUp } from "@phosphor-icons/react";

const PERIODS = [
  ["week", "Weekly"],
  ["month", "Monthly"],
  ["quarter", "Quarterly"],
];

const DarkTooltip = ({ active, payload, label, period }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-sm border border-[#CAD1DB] bg-[#FFFFFF] px-3 py-2 text-xs shadow-xl">
      <div className="mb-1 font-mono text-[#64748B]">{fmtPeriod(label, period)}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-4 font-mono">
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2 w-2" style={{ background: p.color }} />
            {p.dataKey}
          </span>
          <span className="text-[#0F172A]">{fmtInt(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

export const TrendsChart = ({ counts, period, onPeriodChange }) => {
  return (
    <div className="col-span-12 border border-[#E3E7EC] bg-[#FFFFFF] p-5 lg:col-span-8">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <TrendUp size={18} weight="duotone" className="text-[#F0323E]" />
          <div>
            <h3 className="text-sm font-semibold text-[#0F172A]">Volume Trends by Stage</h3>
            <p className="pf-label mt-0.5">Order lines · order date · OR / OQ / OF</p>
          </div>
        </div>
        <div className="flex rounded-sm border border-[#E3E7EC] p-0.5" data-testid="trends-period-toggle">
          {PERIODS.map(([k, l]) => (
            <button
              key={k}
              data-testid={`trends-period-${k}`}
              onClick={() => onPeriodChange(k)}
              className={`rounded-sm px-3 py-1 text-xs font-medium transition-colors ${
                period === k ? "bg-[#F0323E] text-white" : "text-[#64748B] hover:text-[#0F172A]"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>
      <div className="h-72" data-testid="trends-chart">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={counts} margin={{ top: 5, right: 8, left: -8, bottom: 0 }}>
            <defs>
              {Object.entries(STAGE).map(([k, v]) => (
                <linearGradient key={k} id={`grad-${k}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={v.color} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={v.color} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid stroke="#E3E7EC" vertical={false} />
            <XAxis dataKey="period" tickFormatter={(v) => fmtPeriod(v, period)} tick={{ fill: "#64748B", fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} minTickGap={16} />
            <YAxis tickFormatter={fmtInt} tick={{ fill: "#64748B", fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} width={44} />
            <Tooltip content={<DarkTooltip period={period} />} />
            <Legend wrapperStyle={{ fontSize: 12, fontFamily: "IBM Plex Mono" }} />
            {Object.entries(STAGE).map(([k, v]) => (
              <Area key={k} type="monotone" dataKey={k} stroke={v.color} strokeWidth={2} fill={`url(#grad-${k})`} />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
